import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEuro, fmtNum } from "@/lib/format";
import Footer from "@/components/Footer";
import FadeIn from "@/components/FadeIn";
import { ArrowLeft, ArrowRight, BarChart3, Eye, Handshake, Home, Scale } from "lucide-react";

const ETAPES = [
  {
    num: "01",
    icon: Scale,
    titre: "L'estimation juste",
    texte: "Transactions réelles DVF de votre rue, comparables récents, visite confidentielle. Le bon prix dès le premier jour — ni bradé, ni surestimé.",
  },
  {
    num: "02",
    icon: Eye,
    titre: "La mise en valeur",
    texte: "Conseils de présentation, photographie soignée, dossier de vente documenté. Un bien de prestige mérite une présentation de prestige.",
  },
  {
    num: "03",
    icon: Handshake,
    titre: "La diffusion confidentielle",
    texte: "Acquéreurs qualifiés et solvables, visites ciblées, discrétion totale. Votre vente ne s'affiche pas : elle se conduit.",
  },
  {
    num: "04",
    icon: BarChart3,
    titre: "La négociation & la signature",
    texte: "Chaque offre analysée, chaque condition défendue, jusqu'au compromis et à l'acte authentique. Un interlocuteur unique, du début à la fin.",
  },
];

export default function VendrePage() {
  const [ventes, setVentes] = useState([]);

  useEffect(() => {
    api
      .get("/ventes-recentes?code_postal=75018")
      .then((r) => setVentes(r.data.ventes))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="vendre-page" className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
      <header className="border-b border-[#D4AF37]/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="vendre-brand-link" className="font-display text-xl text-[#F5F3EF]">
            Valeur <span className="italic text-[#D4AF37]">Prestige</span>
          </Link>
          <Link to="/estimation" data-testid="vendre-cta-estimation" className="btn-gold !px-5 !py-2.5 !text-xs">
            Estimer mon bien <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/[0.06] blur-[120px]" />
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]/80">Vente exclusive · Prestige</p>
            <h1 className="font-display mt-6 max-w-3xl text-4xl font-light leading-[1.1] tracking-tight text-[#F5F3EF] sm:text-5xl lg:text-6xl">
              Vendre au <em className="text-[#D4AF37]">juste prix.</em>
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
              À Paris comme dans toute l'Île-de-France, une vente réussie commence par une
              estimation documentée. La nôtre s'appuie sur les transactions réelles de votre
              secteur — puis un conseiller dédié conduit votre vente, du premier jour à la signature.
            </p>
            <Link to="/estimation" data-testid="vendre-hero-cta" className="btn-gold mt-10">
              Commencer par une estimation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section data-testid="vendre-methode" className="border-t border-stone-800/60">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]/80">La méthode</p>
            <h2 className="font-display mt-4 text-2xl font-normal tracking-tight text-amber-100 sm:text-3xl lg:text-4xl">
              Quatre temps, <em className="text-[#D4AF37]">une signature.</em>
            </h2>
          </FadeIn>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {ETAPES.map((e, i) => (
              <FadeIn key={e.num} delay={i * 0.08}>
                <div
                  data-testid={`vendre-etape-${e.num}`}
                  className="group h-full border border-stone-800 bg-[#141417] p-8 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40"
                >
                  <span className="outline-number font-display text-5xl font-light">{e.num}</span>
                  <e.icon size={20} className="mt-6 text-[#D4AF37]" strokeWidth={1.5} />
                  <h3 className="font-display mt-4 text-xl font-medium text-amber-200/90">{e.titre}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-400">{e.texte}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="vendre-marche" className="border-t border-stone-800/60 bg-[#0e0e11]">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]/80">Le marché en ce moment</p>
            <h2 className="font-display mt-4 max-w-2xl text-2xl font-normal tracking-tight text-amber-100 sm:text-3xl lg:text-4xl">
              Les dernières ventes <em className="text-[#D4AF37]">du 18e arrondissement.</em>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-400">
              Transactions réellement enregistrées — base officielle DVF (Demandes de Valeurs
              Foncières, data.gouv.fr). C'est sur ces chiffres, pas sur des annonces, que reposent
              nos estimations, dans Paris et toute l'Île-de-France.
            </p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="mt-10 overflow-x-auto border border-stone-800">
              <table data-testid="vendre-ventes-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-stone-800 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    <th className="px-5 py-4">Adresse</th>
                    <th className="px-5 py-4">Type</th>
                    <th className="px-5 py-4">Surface</th>
                    <th className="px-5 py-4">Prix</th>
                    <th className="px-5 py-4">€/m²</th>
                    <th className="px-5 py-4">Vente</th>
                  </tr>
                </thead>
                <tbody>
                  {ventes.map((v, i) => (
                    <tr key={i} data-testid={`vendre-vente-${i}`} className="border-b border-stone-800/60 text-stone-300 last:border-0">
                      <td className="px-5 py-4">{v.adresse}</td>
                      <td className="px-5 py-4">{v.type}</td>
                      <td className="px-5 py-4">{fmtNum(v.surface)} m²</td>
                      <td className="px-5 py-4">{fmtEuro(v.prix)}</td>
                      <td className="px-5 py-4">{fmtNum(v.prix_m2)} €</td>
                      <td className="px-5 py-4">{v.date.slice(5, 7)}/{v.date.slice(0, 4)}</td>
                    </tr>
                  ))}
                  {ventes.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-stone-500">
                        Chargement des transactions…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-14 flex flex-col items-center justify-between gap-8 border border-[#D4AF37]/25 bg-[#141417] p-10 text-center lg:flex-row lg:text-left">
              <div className="flex items-center gap-5">
                <Home size={26} className="shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl text-amber-100 sm:text-2xl">Et si le prochain bien vendu était le vôtre ?</h3>
                  <p className="mt-2 text-sm text-stone-400">Trois minutes de questionnaire, une estimation documentée, zéro engagement.</p>
                </div>
              </div>
              <Link to="/estimation" data-testid="vendre-marche-cta" className="btn-gold shrink-0">
                Estimer mon bien <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-8">
        <Link to="/" data-testid="vendre-back-home" className="btn-ghost !text-xs">
          <ArrowLeft size={14} /> Retour à l'accueil
        </Link>
      </div>
      <Footer />
    </div>
  );
}
